import React, { useState, useEffect } from 'react';
import { getRequest, postRequest, putRequest } from './UrlRequest';
import { useLocation } from 'react-router-dom';

const DeviceStatusForm = ({ patientId, isListView }) => {
  const [formData, setFormData] = useState({
    diagnosis: '',
    surgery_date: '',
    device_type: '',
    insertion_approach: '',
    monitoring_enable: '',
    mri_enable: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deviceStatusList, setDeviceStatusList] = useState([]);
  const [editStatusId, setEditStatusId] = useState(null); // 수정할 기기 상태 ID 저장
  const [showAllDevices, setShowAllDevices] = useState(false);
  const location = useLocation();
  const application = location.state?.application || 'hearton';
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1366px)');
    const handleResize = (e) => setIsSmallScreen(e.matches);
    
    setIsSmallScreen(mediaQuery.matches);
    mediaQuery.addListener(handleResize);
    
    return () => mediaQuery.removeListener(handleResize);
  }, []);

  // 기기 상태 리스트 가져오기
  const fetchDeviceStatusList = async () => {
    try {
      setLoading(true);
      setError(null); // 요청 시작 시 에러 상태 초기화
      const token = localStorage.getItem('accessToken');
      const data = await getRequest(application, `/doctor/${patientId}/device-statuses`, token);
      const formattedList = data.device_statuses.map((item) => {
        const endDate = item.interval.end_date === '현재' ? new Date().toISOString().slice(0, 10) : item.interval.end_date;
        return {
          id: item.device_status.device_status_id,
          startDate: item.interval.start_date,
          endDate: endDate,
          diagnosis: item.device_status.diagnosis,
          deviceType: item.device_status.device_type,
          insertionApproach: item.device_status.insertion_approach,
          monitoring_enable: item.device_status.monitoring_enable,
          mri_enable: item.device_status.mri_enable
        };
      }).sort((a, b) => new Date(b.startDate) - new Date(a.startDate)); // 날짜 내림차순 정렬 추가
      setDeviceStatusList(formattedList);
    } catch (error) {
      //console.error('Error fetching device statuses:', error);
      setError('기기 상태 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 초기 데이터 로드
  useEffect(() => {
    if (patientId) {
      fetchDeviceStatusList();
    }
  }, [patientId]);

  // 입력값 변경 처리
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // 수정 버튼 클릭 시 수정 모드로 전환하고 폼 데이터 설정
  const handleEditClick = (status) => {
    setEditStatusId(status.id);
    setFormData({
      diagnosis: status.diagnosis,
      surgery_date: status.startDate,
      device_type: status.deviceType,
      insertion_approach: status.insertionApproach,
      monitoring_enable: status.monitoring_enable,
      mri_enable: status.mri_enable,
    });
  };

  // 수정 폼 닫기
  const handleCancelEdit = () => {
    setEditStatusId(null);
    setFormData({
      diagnosis: '',
      surgery_date: '',
      device_type: '',
      insertion_approach: '',
      monitoring_enable: '',
      mri_enable: ''
    });
  };

  // 수정 폼 제출
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('accessToken');
      await putRequest(application, `/doctor/device-status/${editStatusId}`, formData, token);
      alert('기기 상태가 성공적으로 수정되었습니다.');
      setEditStatusId(null); // 수정 모드 종료
      fetchDeviceStatusList(); // 목록 갱신
    } catch (error) {
      //console.error('Error updating device status:', error);
      setError('기기 상태 수정에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 작성 폼 제출
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('accessToken');
      await postRequest(application, `/doctor/${patientId}/device-status`, formData, token);
      alert('기기 상태가 성공적으로 작성되었습니다.');
      setFormData({
        diagnosis: '',
        surgery_date: '',
        device_type: '',
        insertion_approach: '',
        monitoring_enable: '',
        mri_enable: ''
      });
      fetchDeviceStatusList(); // 목록 갱신
    } catch (error) {
      //console.error('Error creating device status:', error);
      setError('기기 상태 작성에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="device-status-container" style={{
      width: '100%',
      height: isListView ? 'calc(100vh - 120px)' : '100%', // 목록 보기일 때만 전체 높이 설정
      //display: 'flex',
      flexDirection: 'column'
    }}>
      {isListView ? (
        <div className="device-status-list" style={{
          width: '50%',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}>
          <h2 style={{ 
            margin: '0',
            fontSize: '16px',
            fontWeight: '500'
          }}>기기 상태 목록</h2>

          {error && (
            <div style={{
              marginBottom: '16px',
              color: 'red',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>{error}</span>
              <button
                onClick={() => {
                  setError(null);  // 즉시 에러 메시지 제거
                  fetchDeviceStatusList();  // API 요청 실행
                }}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#e53e3e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                다시 시도
              </button>
            </div>
          )}
          {loading ? (
            <div style={{
              textAlign: 'center',
              padding: '20px',
              color: '#4a5568'
            }}>로딩 중...</div>
          ) : (
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              {deviceStatusList.length > 0 && (
                <div style={{ height: '100%' }}>
                  {!showAllDevices ? (
                    <>
                      {deviceStatusList.length > 1 && (
                        <div style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          marginBottom: '16px'
                        }}>
                          <button
                            onClick={() => setShowAllDevices(true)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#48bb78',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                          >
                            모든 기록 보기
                          </button>
                        </div>
                      )}
                      <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '24px',
                        marginBottom: '16px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        border: '1px solid #e2e8f0',
                        height: '200px'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '8px'
                        }}>
                          <h3 style={{
                            fontSize: '16px',
                            margin: '0px',
                            fontWeight: '500',
                            color: '#2d3748'
                          }}>{deviceStatusList[0].diagnosis}</h3>
                          <button 
                            onClick={() => handleEditClick(deviceStatusList[0])}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#4299e1',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                          >
                            수정
                          </button>
                        </div>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          gap: '12px',
                          fontSize: '16px',
                          color: '#4a5568',
                          height: '140px'
                        }}>
                          <p style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                            margin: '0px'
                          }}>
                            <span style={{ fontWeight: '500' }}>기간:</span>
                            {deviceStatusList[0].startDate} ~ {deviceStatusList[0].endDate}
                          </p>
                          <p style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                            margin: '0px'
                          }}>
                            <span style={{ fontWeight: '500' }}>기기 유형:</span>
                            {deviceStatusList[0].deviceType}
                          </p>
                          <p style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                            margin: '0px'
                          }}>
                            <span style={{ fontWeight: '500' }}>삽입 접근 방식:</span>
                            {deviceStatusList[0].insertionApproach}
                          </p>
                          <div style={{
                            display: 'flex',
                            gap: '20px',
                            margin: '0px'
                          }}>
                            <p style={{
                              display: 'flex',
                              gap: '8px',
                              alignItems: 'center',
                              margin: '0px'
                            }}>
                              <span style={{ fontWeight: '500' }}>모니터링:</span>
                              {deviceStatusList[0].monitoring_enable}
                            </p>
                            <p style={{
                              display: 'flex',
                              gap: '8px',
                              alignItems: 'center',
                              margin: '0px'
                            }}>
                              <span style={{ fontWeight: '500' }}>MRI:</span>
                              {deviceStatusList[0].mri_enable}
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '16px'
                      }}>
                        <h3 style={{
                          fontSize: '18px',
                          margin: '0px',
                          fontWeight: '500',
                          color: '#2d3748'
                        }}>전체 기기 상태 기록</h3>
                        <button
                          onClick={() => setShowAllDevices(false)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#48bb78',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          최신 기록만 보기
                        </button>
                      </div>
                      <div style={{
                        overflowY: 'auto',
                        maxHeight: 'calc(100% - 60px)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                      }}>
                        {deviceStatusList.map((status) => (
                          <div key={status.id} style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            padding: '16px',
                            marginBottom: '8px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            border: '1px solid #e2e8f0'
                          }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '8px'
                            }}>
                              <h3 style={{
                                fontSize: '16px',
                                margin: '0px',
                                fontWeight: '500',
                                color: '#2d3748'
                              }}>{status.diagnosis}</h3>
                              <button 
                                onClick={() => handleEditClick(status)}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#4299e1',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '14px'
                                }}
                              >
                                수정
                              </button>
                            </div>

                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(2, 1fr)',
                              gap: '8px',
                              fontSize: '14px',
                              color: '#4a5568'
                            }}>
                              <p style={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                margin: '0px'
                              }}>
                                <span style={{ fontWeight: '500' }}>기간:</span>
                                {status.startDate} ~ {status.endDate}
                              </p>
                              <p style={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                margin: '0px'
                              }}>
                                <span style={{ fontWeight: '500' }}>기기 유형:</span>
                                {status.deviceType}
                              </p>
                              <p style={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                margin: '0px'
                              }}>
                                <span style={{ fontWeight: '500' }}>삽입 접근 방식:</span>
                                {status.insertionApproach}
                              </p>
                              <div style={{
                                display: 'flex',
                                gap: '20px',
                                margin: '0px'
                              }}>
                                <p style={{
                                  display: 'flex',
                                  gap: '8px',
                                  alignItems: 'center',
                                  margin: '0px'
                                }}>
                                  <span style={{ fontWeight: '500' }}>모니터링:</span>
                                  {status.monitoring_enable}
                                </p>
                                <p style={{
                                  display: 'flex',
                                  gap: '8px',
                                  alignItems: 'center',
                                  margin: '0px'
                                }}>
                                  <span style={{ fontWeight: '500' }}>MRI:</span>
                                  {status.mri_enable}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="device-status-container" style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          height: '100%',
          overflow: 'hidden',
          // padding: '20px',
          backgroundColor: '#f7fafc'
        }}>
          {/* 상단: 기기 상태 작성 */}
          <div className="device-status-form" style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            // width: '100%',
            border: '1px solid #e2e8f0'
          }}>
            <h2 style={{ 
              margin: '0 0 24px 0',
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#2d3748',
              borderBottom: '2px solid #4299e1',
              paddingBottom: '12px'
            }}>{editStatusId ? '기기 상태 수정' : '기기 상태 작성'}</h2>
        
            <form onSubmit={editStatusId ? handleEditSubmit : handleCreateSubmit} style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '20px',
              alignItems: 'start'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                <label style={{ 
                  fontSize: '1rem',
                  fontWeight: '500',
                  color: '#4a5568',
                  marginBottom: '4px'
                }}>
                  진단명
                </label>
                <input
                  type="text"
                  name="diagnosis"
                  value={formData.diagnosis}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    transition: 'all 0.2s',
                    backgroundColor: '#f8fafc',
                    boxSizing: 'border-box',
                    '&:focus': {
                      borderColor: '#4299e1',
                      boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.2)',
                      outline: 'none'
                    }
                  }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                <label style={{ 
                  fontSize: '1rem',
                  fontWeight: '500',
                  color: '#4a5568',
                  marginBottom: '4px'
                }}>
                  수술 날짜
                </label>
                <input
                  type="date"
                  name="surgery_date"
                  value={formData.surgery_date}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    transition: 'all 0.2s',
                    backgroundColor: '#f8fafc',
                    boxSizing: 'border-box',
                    '&:focus': {
                      borderColor: '#4299e1',
                      boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.2)',
                      outline: 'none'
                    }
                  }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ 
                  fontSize: '1rem',
                  fontWeight: '500',
                  color: '#4a5568',
                  marginBottom: '4px'
                }}>
                  기기 유형
                </label>
                <select
                  name="device_type"
                  value={formData.device_type}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    transition: 'all 0.2s',
                    backgroundColor: '#f8fafc',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%234a5568'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '16px',
                    '&:focus': {
                      borderColor: '#4299e1',
                      boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.2)',
                      outline: 'none'
                    }
                  }}
                >
                  <option value="">선택하세요</option>
                  <option value="심박기(Pacemaker)">심박기(Pacemaker)</option>
                  <option value="삽입형제세동기(ICD)">삽입형제세동기(ICD)</option>
                  <option value="심장재동기화치료기(CRT-D)">심장재동기화치료기(CRT-D)</option>
                  <option value="심장재동기화치료기(CRT-P)">심장재동기화치료기(CRT-P)</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ 
                  fontSize: '1rem',
                  fontWeight: '500',
                  color: '#4a5568',
                  marginBottom: '4px'
                }}>
                  삽입 접근 방식
                </label>
                <select
                  name="insertion_approach"
                  value={formData.insertion_approach}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    transition: 'all 0.2s',
                    backgroundColor: '#f8fafc',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%234a5568'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '16px',
                    '&:focus': {
                      borderColor: '#4299e1',
                      boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.2)',
                      outline: 'none'
                    }
                  }}
                >
                  <option value="">선택하세요</option>
                  <option value="개흉">개흉</option>
                  <option value="경정맥">경정맥</option>
                  <option value="피하">피하</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ 
                  fontSize: '1rem',
                  fontWeight: '500',
                  color: '#4a5568',
                  marginBottom: '4px'
                }}>
                  모니터링 가능 여부
                </label>
                <select
                  name="monitoring_enable"
                  value={formData.monitoring_enable}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    transition: 'all 0.2s',
                    backgroundColor: '#f8fafc',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%234a5568'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '16px',
                    '&:focus': {
                      borderColor: '#4299e1',
                      boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.2)',
                      outline: 'none'
                    }
                  }}
                >
                  <option value="">선택하세요</option>
                  <option value="유">유</option>
                  <option value="무">무</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ 
                  fontSize: '1rem',
                  fontWeight: '500',
                  color: '#4a5568',
                  marginBottom: '4px'
                }}>
                  MRI 가능 여부
                </label>
                <select
                  name="mri_enable"
                  value={formData.mri_enable}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    transition: 'all 0.2s',
                    backgroundColor: '#f8fafc',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%234a5568'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '16px',
                    '&:focus': {
                      borderColor: '#4299e1',
                      boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.2)',
                      outline: 'none'
                    }
                  }}
                >
                  <option value="">선택하세요</option>
                  <option value="유">유</option>
                  <option value="무">무</option>
                </select>
              </div>

              <div style={{ 
                gridColumn: '1 / -1',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                marginTop: '12px'
              }}>
                {editStatusId && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditStatusId(null);
                      setFormData({
                        diagnosis: '',
                        surgery_date: '',
                        device_type: '',
                        insertion_approach: '',
                        monitoring_enable: '',
                        mri_enable: ''
                      });
                    }}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#e2e8f0',
                      color: '#4a5568',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: '500',
                      transition: 'all 0.2s',
                      '&:hover': {
                        backgroundColor: '#cbd5e0'
                      }
                    }}
                  >
                    취소
                  </button>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    padding: '10px 24px',
                    backgroundColor: '#4299e1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    opacity: loading ? 0.7 : 1,
                    '&:hover': {
                      backgroundColor: '#3182ce'
                    }
                  }}
                >
                  {loading ? '처리 중...' : (editStatusId ? '수정하기' : '작성하기')}
                </button>
              </div>
            </form>
            
            {error && (
              <div style={{
                marginTop: '16px',
                padding: '12px',
                backgroundColor: '#fff5f5',
                color: '#c53030',
                borderRadius: '8px',
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                {error}
              </div>
            )}
          </div>
          {/* 하단: 기기 상태 목록 */}
          <div className="device-status-list" style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto'
          }}>
            {error && (
              <div style={{
                marginBottom: '16px',
                color: 'red',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>{error}</span>
                <button
                  onClick={() => {
                    setError(null);  // 즉시 에러 메시지 제거
                    fetchDeviceStatusList();  // API 요청 실행
                  }}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#e53e3e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  다시 시도
                </button>
              </div>
            )}
            {loading && <p>로딩 중...</p>}
            {error && <p style={{ color: 'red' }}>{error}</p>}
            {!loading && !error && deviceStatusList.length === 0 && (
              <p>기기 상태 정보가 없습니다.</p>
            )}
            {!loading && !error && deviceStatusList.length > 0 && (
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto'
              }}>
                <div style={{ height: '100%', overflowY: 'auto' }}>
                  {!showAllDevices ? (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      overflowY: 'auto'
                    }}>
                      {deviceStatusList.length > 1 && (
                        <div style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          marginBottom: '16px'
                        }}>
                          <button
                            onClick={() => setShowAllDevices(true)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#48bb78',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                          >
                            모든 기록 보기
                          </button>
                        </div>
                      )}
                      <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '24px',
                        marginBottom: '16px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        border: '1px solid #e2e8f0',
                        height: '200px'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '8px'
                        }}>
                          <h3 style={{
                            fontSize: '16px',
                            margin: '0px',
                            fontWeight: '500',
                            color: '#2d3748'
                          }}>{deviceStatusList[0].diagnosis}</h3>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button 
                              onClick={() => handleEditClick(deviceStatusList[0])}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#4299e1',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              수정
                            </button>
                          </div>
                        </div>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          gap: '12px',
                          fontSize: '16px',
                          color: '#4a5568',
                          height: '140px'
                        }}>
                          <p style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                            margin: '0px'
                          }}>
                            <span style={{ fontWeight: '500' }}>기간:</span>
                            {deviceStatusList[0].startDate} ~ {deviceStatusList[0].endDate}
                          </p>
                          <p style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                            margin: '0px'
                          }}>
                            <span style={{ fontWeight: '500' }}>기기 유형:</span>
                            {deviceStatusList[0].deviceType}
                          </p>
                          <p style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                            margin: '0px'
                          }}>
                            <span style={{ fontWeight: '500' }}>삽입 접근 방식:</span>
                            {deviceStatusList[0].insertionApproach}
                          </p>
                          <div style={{
                            display: 'flex',
                            gap: '20px',
                            margin: '0px'
                          }}>
                            <p style={{
                              display: 'flex',
                              gap: '8px',
                              alignItems: 'center',
                              margin: '0px'
                            }}>
                              <span style={{ fontWeight: '500' }}>모니터링:</span>
                              {deviceStatusList[0].monitoring_enable}
                            </p>
                            <p style={{
                              display: 'flex',
                              gap: '8px',
                              alignItems: 'center',
                              margin: '0px'
                            }}>
                              <span style={{ fontWeight: '500' }}>MRI:</span>
                              {deviceStatusList[0].mri_enable}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      overflowY: 'auto'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '16px'
                      }}>
                        <h3 style={{
                          fontSize: '18px',
                          margin: '0px',
                          fontWeight: '500',
                          color: '#2d3748'
                        }}>전체 기기 상태 기록</h3>
                        <button
                          onClick={() => setShowAllDevices(false)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#48bb78',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          최신 기록만 보기
                        </button>
                      </div>
                      <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                      }}>
                        {deviceStatusList.map((status) => (
                          <div key={status.id} style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            padding: '16px',
                            marginBottom: '8px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            border: '1px solid #e2e8f0'
                          }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '8px'
                            }}>
                              <h3 style={{
                                fontSize: '16px',
                                margin: '0px',
                                fontWeight: '500',
                                color: '#2d3748'
                              }}>{status.diagnosis}</h3>
                              <button 
                                onClick={() => handleEditClick(status)}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#4299e1',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '14px'
                                }}
                              >
                                수정
                              </button>
                            </div>

                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(2, 1fr)',
                              gap: '8px',
                              fontSize: '14px',
                              color: '#4a5568'
                            }}>
                              <p style={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                margin: '0px'
                              }}>
                                <span style={{ fontWeight: '500' }}>기간:</span>
                                {status.startDate} ~ {status.endDate}
                              </p>
                              <p style={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                margin: '0px'
                              }}>
                                <span style={{ fontWeight: '500' }}>기기 유형:</span>
                                {status.deviceType}
                              </p>
                              <p style={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                margin: '0px'
                              }}>
                                <span style={{ fontWeight: '500' }}>삽입 접근 방식:</span>
                                {status.insertionApproach}
                              </p>
                              <div style={{
                                display: 'flex',
                                gap: '20px',
                                margin: '0px'
                              }}>
                                <p style={{
                                  display: 'flex',
                                  gap: '8px',
                                  alignItems: 'center',
                                  margin: '0px'
                                }}>
                                  <span style={{ fontWeight: '500' }}>모니터링:</span>
                                  {status.monitoring_enable}
                                </p>
                                <p style={{
                                  display: 'flex',
                                  gap: '8px',
                                  alignItems: 'center',
                                  margin: '0px'
                                }}>
                                  <span style={{ fontWeight: '500' }}>MRI:</span>
                                  {status.mri_enable}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceStatusForm;
